'use client';
import { useT } from '../../lib/i18n';
import { Icon } from '../Icon';
import type { ListPrefs, SortKey } from './listPrefs';

/** 정렬 탭 — 인기순이 첫 칸이다(§④-d) */
const SORTS: { key: SortKey; icon: string; iconDesc: string }[] = [
  { key: 'popular', icon: 'flame', iconDesc: 'flame' },
  { key: 'latest', icon: 'clock-arrow-up', iconDesc: 'clock-arrow-down' },
  { key: 'name', icon: 'arrow-up-a-z', iconDesc: 'arrow-down-a-z' },
  { key: 'price', icon: 'arrow-up-wide-narrow', iconDesc: 'arrow-down-wide-narrow' },
];

/**
 * 정렬 탭 + 목록 전환 — 가이드 `sort-tabs` · `view-toggle`.
 *
 * **한 벌만 둔다**(2026-08-23) — 목록 화면(`CourseFilters`)과 첫 화면·프로모션 묶음
 * (`CourseGrid`)이 같은 것을 쓴다. 두 벌이 되는 순간 아이콘·방향 규칙이 갈린다.
 *
 * 둘은 **따로 켠다** — 설정에서도, 자리(섹션·묶음)에서도(§목록 전환/정렬 분리).
 * 둘 다 꺼져 있으면 줄 자체를 그리지 않는다.
 */
export function ListControls({ prefs, onPrefs, viewToggle, sortControls, left }: {
  prefs: ListPrefs;
  onPrefs: (next: ListPrefs) => void;
  viewToggle: boolean;
  sortControls: boolean;
  /**
   * 줄 왼쪽 칸 — 지금은 구독 보기 토글이 온다(현행도 `list_btn` 왼쪽에 `float:left` 로 뒀다).
   * 여기가 차면 줄은 양끝 배치가 된다. 비면 예전처럼 오른쪽으로 몰린다.
   */
  left?: React.ReactNode;
}) {
  const t = useT();
  if (!viewToggle && !sortControls && !left) return null;

  /** 같은 탭을 다시 누르면 방향이 뒤집힌다. 인기순은 매긴 차례가 곧 순서라 방향이 없다 */
  const clickSort = (key: SortKey) => {
    if (key === 'popular') { onPrefs({ ...prefs, sort: 'popular' }); return; }
    if (prefs.sort === key) { onPrefs({ ...prefs, dir: prefs.dir === 'asc' ? 'desc' : 'asc' }); return; }
    onPrefs({ ...prefs, sort: key, dir: key === 'latest' ? 'desc' : 'asc' });
  };

  /*
   * 줄은 늘 `jc-end` 다 — 왼쪽 칸은 **자기 오른쪽 여백으로 밀어낸다**(`list-ctl-left`).
   * `jc-between` 으로 바꿨더니 정렬 탭과 뷰 토글이 형제라 **셋이 흩어졌다**(2026-09-09
   * 사용자 지적). 오른쪽 둘을 감싸는 상자를 만들면 왼쪽 칸이 없을 때의 마크업이 달라져
   * 가이드 배치가 흔들린다. 자동 여백이면 자식 수를 건드리지 않는다.
   */
  return (
    <div className="d-flex ai-center jc-end mb15">
      {left && <div className="list-ctl-left">{left}</div>}
      {sortControls && (
        <div className="sort-tabs">
          {SORTS.map((s) => {
            const on = prefs.sort === s.key;
            // 고른 탭만 방향을 드러낸다 — 아이콘이 뒤집히고 라벨도 "최신순↔오래된순" 으로 바뀐다
            const flipped = on && s.key !== 'popular' && prefs.dir === 'desc';
            const label = s.key === 'popular'
              ? t('prd.sort.popular')
              : t(`prd.sort.${s.key}.${on ? prefs.dir : (s.key === 'latest' ? 'desc' : 'asc')}`);
            return (
              <button key={s.key} className={`sort-tab${on ? ' active' : ''}`} onClick={() => clickSort(s.key)}>
                <Icon name={flipped ? s.iconDesc : s.icon} />{label}
              </button>
            );
          })}
        </div>
      )}
      {viewToggle && (
        <div className="view-toggle">
          <button className={`view-btn${prefs.view === 'card' ? ' active' : ''}`}
            onClick={() => onPrefs({ ...prefs, view: 'card' })} title={t('prd.view.card')}>
            <Icon name="layout-grid" />
          </button>
          <button className={`view-btn${prefs.view === 'list' ? ' active' : ''}`}
            onClick={() => onPrefs({ ...prefs, view: 'list' })} title={t('prd.view.list')}>
            <Icon name="list" />
          </button>
        </div>
      )}
    </div>
  );
}
